import { ensureToken, fetchJsonWithAuth, getStoredToken } from './authApi';
import { AUTH_URL, JOBS_URL } from './apiConfig';

// ── Jobs via jobsapi/v1 (user auth) ──────────────────────────────────────────

export async function getJobs({
  jobStatus = 'active',
  jobRecruitmentStatus = 'unread',
  limit = 50,
  offset = 0,
  userId = 1,
  sortBy = 'id',
} = {}) {
  const token = await ensureToken();
  const params = new URLSearchParams({
    jobStatus, jobRecruitmentStatus,
    limit: String(limit), offset: String(offset),
    userId: String(userId), sortBy,
  });
  const res = await fetch(`${JOBS_URL}/jobs?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  const json = await res.json();
  return json.data ?? json;
}

export async function getJobDetailView(id) {
  const token = await ensureToken();
  const params = new URLSearchParams({ jobId: String(id) });
  const res = await fetch(`${JOBS_URL}/jobs?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  const json = await res.json();
  return json.data ?? json;
}

export async function uploadJobAttachment(recordId, file) {
  const token = await ensureToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('jobId', recordId);
  const res = await fetch(`${JOBS_URL}/jobs/${recordId}/attachment`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || `API ${res.status}: ${res.statusText}`);
  }
  const json = await res.json();
  return json.data ?? json;
}

// ── Jobs via authapi/v1 (service auth — auto-refreshes with stored token) ────
// Used by DescriptionBlock for the job description edit flow.

const SERVICE_CREDENTIALS = {
  email: 'zinnext@realtekconsulting.net',
  user_pwd: 'Admin@123*',
};

let serviceLoginPromise = null;

async function serviceLogin() {
  if (serviceLoginPromise) return serviceLoginPromise;
  serviceLoginPromise = fetch(`${AUTH_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(SERVICE_CREDENTIALS),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Login failed: ${res.status}`);
      const json = await res.json();
      const token = json.token ?? json.access_token ?? json.data?.token ?? json.data?.access_token;
      if (!token) throw new Error('No token in login response');
      localStorage.setItem('authToken', token);
      return token;
    })
    .finally(() => { serviceLoginPromise = null; });
  return serviceLoginPromise;
}

async function getServiceToken() {
  const token = getStoredToken() ?? localStorage.getItem('authToken');
  if (token) return token;
  return serviceLogin();
}

async function authApiGet(path) {
  let token = await getServiceToken();
  let res = await fetch(`${AUTH_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    localStorage.removeItem('authToken');
    token = await serviceLogin();
    res = await fetch(`${AUTH_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
  }
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  const json = await res.json();
  return json.data ?? json;
}

async function authApiPut(path, body) {
  let token = await getServiceToken();
  let res = await fetch(`${AUTH_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    localStorage.removeItem('authToken');
    token = await serviceLogin();
    res = await fetch(`${AUTH_URL}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  }
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

export async function getAllJobs() {
  return authApiGet('/jobs');
}

export async function getJobById(id) {
  return authApiGet(`/jobs/${id}`);
}

export async function updateJobDescription(id, rawDescription) {
  return authApiPut(`/jobs/${id}`, { rawDescription });
}

export async function getJobDetailedView(module, id) {
  const token = await getServiceToken();
  const res = await fetch(`${AUTH_URL}/detailedView?module=${module}&id=${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
  return res.json();
}

export async function getSourceCandidates(jobId, options = {}) {
  const token = await ensureToken();

  const {
    offset = 0,
    limit = 10,
    booleanQuery = '',
    skills = [],
    location = '',
    jobTitle = '',
    minExperience = '',
    maxExperience = '',
    hiringStage = '',
    hiringStageType = '',
    sortBy = 'relevance',
    sortType = 'asc',
    searchFilter = {},
    diceFilter = {},
    searchQuery = '',
    sourceType = 'internal',
    selectedSource = ['internal'],
    userId = 0,
  } = options;

  const tenantId = localStorage.getItem('tenantId') || '';
  const businessId = localStorage.getItem('businessId') || '';
  const businessUnitId = localStorage.getItem('businessUnitId') || '';

  const payload = {
    tenantId,
    businessId,
    businessUnitId,
    jobId,
    filter: {
      skills,
      location: location ? [location] : [],
      jobTitle: jobTitle ? [jobTitle] : [],
      experienceFrom: minExperience?.toString() || '',
      experienceTo: maxExperience?.toString() || '',
    },
    hiringStage,
    hiringStageType,
    sortBy,
    sortType,
    booleanQuery,
    searchFilter,
    diceFilter,
    searchQuery,
    sourceType,
    selectedSource,
    userId,
    offset,
    limit,
  };

  const response = await fetch(
    `${JOBS_URL}/source/hiring-candidate-list?offset=${offset}&limit=${limit}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}

export async function getJobDescription(jobId) {
  const token = await ensureToken();

  const response = await fetch(
    `${JOBS_URL}/jobs?jobId=${jobId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}
